"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";

export function NavigationProgress() {
    const pathname = usePathname();
    const [width, setWidth] = useState(0);
    const [visible, setVisible] = useState(false);
    const [mounted, setMounted] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval>>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
    const prevPathname = useRef(pathname);

    useEffect(() => setMounted(true), []);

    const start = useCallback(() => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        if (timerRef.current) clearInterval(timerRef.current);

        setVisible(true);
        setWidth(0);

        let w = 0;
        timerRef.current = setInterval(() => {
            w += (90 - w) * 0.08 + Math.random() * 2;
            if (w > 90) w = 90;
            setWidth(w);
        }, 250);
    }, []);

    const done = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        setWidth(100);
        hideTimerRef.current = setTimeout(() => {
            setVisible(false);
            setWidth(0);
        }, 300);
    }, []);

    // Complete when pathname changes
    useEffect(() => {
        if (prevPathname.current !== pathname) {
            done();
        }
        prevPathname.current = pathname;
    }, [pathname, done]);

    // Intercept internal link clicks
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const a = (e.target as HTMLElement).closest("a");
            if (!a) return;

            const href = a.getAttribute("href");
            if (!href) return;
            if (href.startsWith("http") || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
            if (a.target === "_blank" || a.hasAttribute("download")) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

            try {
                const url = new URL(href, window.location.origin);
                if (url.pathname !== window.location.pathname) {
                    start();
                }
            } catch {
                // Invalid URL, skip
            }
        };

        document.addEventListener("click", handler, true);
        return () => document.removeEventListener("click", handler, true);
    }, [start]);

    // Handle back/forward navigation
    useEffect(() => {
        const handler = () => start();
        window.addEventListener("popstate", handler);
        return () => window.removeEventListener("popstate", handler);
    }, [start]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        };
    }, []);

    if (!mounted || !visible) return null;

    return createPortal(
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                height: "2.5px",
                zIndex: 2147483647,
                pointerEvents: "none",
            }}
        >
            <div
                style={{
                    height: "100%",
                    width: `${width}%`,
                    background: "var(--primary-foreground)",
                    opacity: width === 100 ? 0 : 0.5,
                    transition: "width 200ms ease-out, opacity 300ms ease-out",
                }}
            />
        </div>,
        document.body,
    );
}
